import React, { useState, useEffect } from "react";
import { Calendar, Phone, Mail, CheckCircle, Clock } from "lucide-react";
import { X } from "lucide-react";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const API_BASE =
  window.location.hostname === "test.maxiomwealth.com"
    ? "https://regular.docker.jama.co.in/app/api"
    : "https://regular.docker.jama.co.in/app/api";

const ScheduleMeeting = ({
  visitorId = null,
  prefilledData = {},
  onClose,
  updateLead,
  onLeadFormSubmit,
}) => {
  // Wizard step management
  const [step, setStep] = useState(1);

  // Form data
  const [meetingType, setMeetingType] = useState("VIDEO");
  const [topic, setTopic] = useState("PFV");
  const [customTopic, setCustomTopic] = useState("");
  const [name, setName] = useState(prefilledData.name || "");
  const [email, setEmail] = useState(prefilledData.email || "");
  const [countryCode, setCountryCode] = useState("+91");
  const [phone, setPhone] = useState(
    prefilledData.mobile || prefilledData.phone || ""
  );
  const [message, setMessage] = useState("");

  // Common country codes
  const countryCodes = [
    { code: "+91", label: "IN +91" },
    { code: "+1", label: "US +1" },
    { code: "+44", label: "UK +44" },
    { code: "+971", label: "AE +971" },
    { code: "+65", label: "SG +65" },
    { code: "+61", label: "AU +61" },
    { code: "+49", label: "DE +49" },
    { code: "+81", label: "JP +81" },
    { code: "+86", label: "CN +86" },
    { code: "+33", label: "FR +33" },
    { code: "+39", label: "IT +39" },
    { code: "+7", label: "RU +7" },
    { code: "+55", label: "BR +55" },
    { code: "+27", label: "ZA +27" },
    { code: "+82", label: "KR +82" },
    { code: "+60", label: "MY +60" },
    { code: "+66", label: "TH +66" },
    { code: "+62", label: "ID +62" },
    { code: "+63", label: "PH +63" },
    { code: "+64", label: "NZ +64" },
    { code: "+852", label: "HK +852" },
    { code: "+353", label: "IE +353" },
    { code: "+974", label: "QA +974" },
    { code: "+966", label: "SA +966" },
    { code: "+968", label: "OM +968" },
    { code: "+973", label: "BH +973" },
    { code: "+965", label: "KW +965" },
  ];

  // OTP state
  const [otpMethod, setOtpMethod] = useState("mobile");
  const [otp, setOtp] = useState("");
  // If phone is already prefilled, skip OTP verification
  const hasPrefilledPhone = !!(prefilledData.mobile || prefilledData.phone);
  const [otpSent, setOtpSent] = useState(hasPrefilledPhone);
  const [otpVerified, setOtpVerified] = useState(hasPrefilledPhone);
  const [otpLoading, setOtpLoading] = useState(false);

  // DateTime state
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [availableSlots, setAvailableSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);

  // Submission state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [bookedMeeting, setBookedMeeting] = useState(null);

  // Countdown timer for auto-booking
  const [countdown, setCountdown] = useState(5);

  // Geolocation state
  const [geoData, setGeoData] = useState({ city: "", state: "", country: "" });

  // Meeting topics with key-value pairs (key for API, label for display)
  const meetingTopics = [
    { key: "PFV", label: "Portfolio Review" },
    { key: "IVS", label: "Investment Strategy" },
    { key: "RTP", label: "Retirement Planning" },
    { key: "TXP", label: "Tax Planning" },
    { key: "WTM", label: "Wealth Management" },
    { key: "OTH", label: "Other" },
  ];

  const sanitizePhoneInput = (value) => {
    return value.replace(/\D/g, "").slice(0, 10);
  };

  const isValidPhone = (value) => /^[1-9]\d{9}$/.test(value);

  const getFullPhone = () => `${countryCode}${phone}`;

  // Send OTP — accepts raw 10-digit number
  const sendOTP = async (rawPhoneDigits = null) => {
    const digits = rawPhoneDigits || phone;

    if (otpMethod === "mobile" && !isValidPhone(digits)) {
      toast.error("Please enter a valid 10-digit mobile number");
      return;
    }
    if (otpMethod === "email" && !email.includes("@")) {
      toast.error("Please enter a valid email address");
      return;
    }

    setOtpLoading(true);
    try {
      const endpoint =
        otpMethod === "mobile"
          ? `${API_BASE}/v2/mobile_otp_verfication/`
          : `${API_BASE}/v2/email_otp_verfication/`;

      const payload =
        otpMethod === "mobile"
          ? { target_mobile_number: digits }
          : { target_email: email };

      const response = await fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (data.resp_code === "100") {
        setOtpSent(true);
        toast.success("OTP sent successfully!");
      } else {
        toast.error(data.message || "Failed to send OTP. Please try again.");
      }
    } catch (error) {
      console.error("OTP send error:", error);
      toast.error("Network error. Please try again.");
    } finally {
      setOtpLoading(false);
    }
  };

  // Verify OTP
  const verifyOTP = async (otpCode = null) => {
    const otpToUse = otpCode || otp;

    if (otpToUse.length !== 4) {
      toast.error("Please enter a 4-digit OTP");
      return;
    }

    setOtpLoading(true);
    try {
      const endpoint =
        otpMethod === "mobile"
          ? `${API_BASE}/v2/mobile_otp_verfication/`
          : `${API_BASE}/v2/email_otp_verfication/`;

      const payload =
        otpMethod === "mobile"
          ? { target_mobile_number: phone, otp: otpToUse }
          : { target_email: email, otp: otpToUse };

      const response = await fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (data.result === true) {
        setOtpVerified(true);
        toast.success("Mobile number verified successfully!");
      } else {
        toast.error(data.message || "Invalid OTP. Please try again.");
      }
    } catch (error) {
      console.error("OTP verify error:", error);
      toast.error("Network error. Please try again.");
    } finally {
      setOtpLoading(false);
    }
  };

  // Fetch geolocation on component mount
  useEffect(() => {
    const fetchGeolocation = async () => {
      try {
        const position = await new Promise((resolve, reject) => {
          navigator.geolocation.getCurrentPosition(resolve, reject, {
            timeout: 10000,
            enableHighAccuracy: false,
            maximumAge: 300000,
          });
        });
        const { latitude, longitude } = position.coords;
        const geoResponse = await fetch(
          `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}`
        );
        if (geoResponse.ok) {
          const geoJson = await geoResponse.json();
          setGeoData((prev) => ({
            ...prev,
            city: geoJson.address?.city || geoJson.address?.town || geoJson.address?.village || "",
            state: geoJson.address?.state || "",
            country: geoJson.address?.country || "",
          }));
          console.log("Geolocation data:", geoJson.address);
        }
      } catch (geoErr) {
        console.warn("Browser geolocation failed, trying IP-based:", geoErr);
        // Fallback to IP-based geolocation
        try {
          const ipGeoResponse = await fetch("https://ipapi.co/json/");
          if (ipGeoResponse.ok) {
            const ipGeoJson = await ipGeoResponse.json();
            setGeoData((prev) => ({
              ...prev,
              city: ipGeoJson.city || "",
              state: ipGeoJson.region || "",
              country: ipGeoJson.country_name || "",
            }));
            console.log("IP Geolocation data:", ipGeoJson);
          }
        } catch (ipGeoErr) {
          console.warn("IP geolocation also failed:", ipGeoErr);
        }
      }
    };
    fetchGeolocation();
  }, []);

  // Fetch available slots when date is selected
  useEffect(() => {
    if (selectedDate && (step === 1 || step === 3)) {
      fetchAvailableSlots();
    }
  }, [selectedDate]);

  // Countdown timer for auto-booking on step 4
  useEffect(() => {
    let timer;
    if (step === 4 && !isSubmitting && countdown > 0) {
      timer = setInterval(() => {
        setCountdown((prev) => prev - 1);
      }, 1000);
    } else if (step === 4 && countdown === 0 && !isSubmitting) {
      bookMeeting();
    }

    // Reset countdown when leaving step 4
    if (step !== 4) {
      setCountdown(5);
    }

    return () => {
      if (timer) clearInterval(timer);
    };
  }, [step, countdown, isSubmitting]);

  const fetchAvailableSlots = async () => {
    if (!selectedDate) return;

    setSlotsLoading(true);
    try {
      const dateStr = `${selectedDate.getFullYear()}-${String(
        selectedDate.getMonth() + 1
      ).padStart(2, "0")}-${String(selectedDate.getDate()).padStart(2, "0")}`;
      const response = await fetch(
        `${API_BASE}/meeting/available-slots/?date=${dateStr}&duration=30&limit=20`
      );

      const data = await response.json();

      if (data.code === "100" && data.slots && data.slots.length > 0) {
        // Make slots unique by slot_datetime
        const uniqueSlots = data.slots.filter(
          (slot, index, self) =>
            index === self.findIndex((s) => s.slot_datetime === slot.slot_datetime)
        );
        setAvailableSlots(uniqueSlots);
      } else {
        setAvailableSlots([]);
        toast.warning("No available slots for this date. Please try another date.");
      }
    } catch (error) {
      console.error("Fetch slots error:", error);
      setAvailableSlots([]);
      toast.error("Failed to fetch available slots. Please try again.");
    } finally {
      setSlotsLoading(false);
    }
  };

  // Book meeting
  const bookMeeting = async () => {
    if (!selectedSlot) {
      toast.error("Please select a time slot");
      return;
    }

    setIsSubmitting(true);
    try {
      const finalTopic = topic === "OTH" ? customTopic : topic;

      // Use the parent's lead form handler if available
      if (onLeadFormSubmit) {
        await onLeadFormSubmit({ name, email, phone, topic: finalTopic });
      }

      const payload = {
        meeting_datetime: selectedSlot.slot_datetime,
        meeting_type: meetingType,
        topic: finalTopic,
        description: message || `Meeting request for ${finalTopic}`,
        name,
        email,
        mobile: getFullPhone(),
        rm_id: null,
        rm_name: null,
        visitor_id: visitorId || undefined,
        placed_from: "MW_WEBSITE_CHATBOT",
        page_url: window.location.href,
        otp_verified_mobile: otpMethod === "mobile" && otpVerified,
        otp_verified_email: otpMethod === "email" && otpVerified,
        city: geoData.city || "",
        state: geoData.state || "",
        country: geoData.country || "",
      };

      const response = await fetch(`${API_BASE}/meeting/book/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (data.code === "100") {
        setBookedMeeting(data.meeting);
        setSubmitStatus("success");

        // Track in Google Analytics if available
        if (window.gtag) {
          window.gtag("event", "meeting_booked", {
            meeting_type: meetingType,
            topic: finalTopic,
          });
        }
      } else {
        toast.error(
          data.msg ||
          "Failed to book meeting. Please try again with different time slot."
        );
        setStep(3);
        setIsSubmitting(false);
      }
    } catch (error) {
      console.error("Book meeting error:", error);
      toast.error("Network error. Please try again.");
      setIsSubmitting(false);
    }
  };

  // Calendar rendering functions
  const handlePrevMonth = () => {
    setCurrentMonth(
      new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1)
    );
    setSelectedDate(null);
    setSelectedSlot(null);
  };

  const handleNextMonth = () => {
    setCurrentMonth(
      new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1)
    );
    setSelectedDate(null);
    setSelectedSlot(null);
  };

  const handleDateClick = (date) => {
    setSelectedDate(date);
    setSelectedSlot(null);
  };

  const formatDate = (date) => {
    if (!date) return "";
    const options = {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    };
    return date.toLocaleDateString("en-US", options);
  };

  useEffect(() => {
    if (step === 3 && updateLead) {
      updateLead({ name: name, email: email, phone: phone });
    }
  }, [step]);

  const monthName = currentMonth.toLocaleString("default", { month: "long" });
  const year = currentMonth.getFullYear();

  const renderCalendar = () => {
    const firstDayOfMonth = new Date(
      currentMonth.getFullYear(),
      currentMonth.getMonth(),
      1
    ).getDay();
    const daysInMonth = new Date(
      currentMonth.getFullYear(),
      currentMonth.getMonth() + 1,
      0
    ).getDate();
    const days = [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Empty cells for days before the first day of the month
    for (let i = 0; i < firstDayOfMonth; i++) {
      days.push(<div key={`empty-${i}`} className="mw-chat-calendar-empty"></div>);
    }

    // Days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(
        currentMonth.getFullYear(),
        currentMonth.getMonth(),
        day
      );
      date.setHours(0, 0, 0, 0);
      const isWeekend = date.getDay() === 0 || date.getDay() === 6;
      const isAvailable = !isWeekend && date >= today;
      const isToday = date.getTime() === today.getTime();
      const isSelected =
        selectedDate && selectedDate.toDateString() === date.toDateString();

      days.push(
        <div
          key={day}
          className={`mw-chat-calendar-day ${isSelected
            ? "selected"
            : isToday
              ? "today"
              : !isAvailable
                ? "disabled"
                : ""
            }`}
          onClick={() => isAvailable && handleDateClick(date)}
        >
          {day}
        </div>
      );
    }

    return days;
  };

  // Render step 1: Meeting Type & Topic
  const renderStep1 = () => (
    <div className="mw-chat-meeting-step">
      <header className="mw-chat-meeting-section-title">Your Information</header>

      <div className="mw-chat-form-group">
        <label className="mw-chat-form-label">Select Topic</label>
        <select
          value={topic}
          onChange={(e) => setTopic(e.target.value)}
          className="mw-chat-form-select"
        >
          {meetingTopics.map((t) => (
            <option key={t.key} value={t.key}>{t.label}</option>
          ))}
        </select>
      </div>

      {topic === "OTH" && (
        <div className="mw-chat-form-group">
          <label className="mw-chat-form-label">Please specify</label>
          <input
            type="text"
            value={customTopic}
            onChange={(e) => setCustomTopic(e.target.value)}
            className="mw-chat-form-input"
            placeholder="Enter your topic"
            required={topic === "Other"}
          />
        </div>
      )}

      <div className="mw-chat-form-group">
        <label className="mw-chat-form-label">Your Name *</label>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="mw-chat-form-input"
          placeholder="Enter your full name"
          required
        />
      </div>

      <div className="mw-chat-form-group">
        <label className="mw-chat-form-label">Your Email *</label>
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="mw-chat-form-input"
          placeholder="Enter your Email address"
          required
        />
      </div>

      <div className="mw-chat-form-group">
        <label className="mw-chat-form-label">Mobile Number *</label>
        <div className="mw-chat-phone-row">
          <select
            value={countryCode}
            onChange={(e) => setCountryCode(e.target.value)}
            className="mw-chat-form-select mw-chat-country-code-select"
            disabled={otpSent && !hasPrefilledPhone}
          >
            {countryCodes.map((c) => (
              <option key={c.code} value={c.code}>{c.label}</option>
            ))}
          </select>
          <input
            type="tel"
            value={phone}
            onChange={(e) => {
              const newPhone = sanitizePhoneInput(e.target.value);
              setPhone(newPhone);

              // Only send OTP if not prefilled and not already sent
              if (
                isValidPhone(newPhone) &&
                !otpSent &&
                !hasPrefilledPhone
              ) {
                sendOTP(newPhone);
              }
            }}
            className="mw-chat-form-input"
            placeholder="Enter 10-digit number"
            maxLength={10}
            disabled={otpSent && !hasPrefilledPhone}
          />
        </div>
      </div>

      {otpSent && !otpVerified && !hasPrefilledPhone && (
        <div className="mw-chat-form-group">
          <label className="mw-chat-form-label">Enter 4-Digit OTP *</label>
          <div className="mw-chat-otp-row">
            <input
              type="text"
              value={otp}
              onChange={(e) => {
                const newOtp = e.target.value.replace(/\D/g, "").slice(0, 4);
                setOtp(newOtp);

                if (newOtp.length === 4) {
                  verifyOTP(newOtp);
                }
              }}
              className="mw-chat-otp-input"
              placeholder="Enter OTP"
              maxLength={4}
              disabled={otpLoading}
            />
            <button
              type="button"
              className="mw-chat-otp-btn"
              onClick={() => {
                setOtp("");
                sendOTP(phone);
              }}
              disabled={otpLoading}
            >
              Resend<br />OTP
            </button>
            <button
              type="button"
              className="mw-chat-otp-btn"
              onClick={() => {
                setOtpSent(false);
                setOtp("");
                setPhone("");
                setCountryCode("+91");
              }}
            >
              Change<br />Number
            </button>
          </div>
          {otpLoading && <p className="mw-chat-form-info">Verifying...</p>}
        </div>
      )}

      {otpVerified && (
        <div className="mw-chat-form-success-box">
          <CheckCircle size={16} />
          <span>Mobile number verified successfully!</span>
        </div>
      )}

      <div className="mw-chat-form-group">
        <label className="mw-chat-form-label">Additional Notes (Optional)</label>
        <textarea
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          rows={2}
          className="mw-chat-form-textarea"
          placeholder="Any specific topics you'd like to discuss"
        ></textarea>
      </div>

      <button
        type="button"
        className={`mw-chat-meeting-btn-primary ${otpVerified && selectedSlot ? "" : "disabled"
          }`}
        onClick={() => {
          if (!otpVerified) {
            toast.error("Please verify your mobile number first");
            return;
          }
          if (!name || !email || (topic === "Other" && !customTopic)) {
            toast.error("Please fill all required fields");
            return;
          }
          if (!email.includes("@")) {
            toast.error("Please enter a valid email address");
            return;
          }
          if (!selectedSlot) {
            toast.error("Please select a date and time slot");
            return;
          }
          setStep(4);
        }}
        disabled={!otpVerified || !selectedSlot}
      >
        {!otpVerified
          ? "Verify Mobile to Continue"
          : !selectedSlot
            ? "Select Time Slot to Continue"
            : "Book Meeting"}
      </button>
    </div>
  );

  // Render step 3: Date & Time Selection
  const renderStep3 = () => (
    <div className="mw-chat-meeting-step">
      {/* Calendar Section */}
      <div className="mw-chat-calendar-container">
        <div className="mw-chat-calendar-header">
          <h3>Select Date</h3>
          <Calendar size={16} />
        </div>

        <div className="mw-chat-calendar-nav">
          <button type="button" onClick={handlePrevMonth} className="mw-chat-calendar-nav-btn">
            &lt;
          </button>
          <h4>{monthName} {year}</h4>
          <button type="button" onClick={handleNextMonth} className="mw-chat-calendar-nav-btn">
            &gt;
          </button>
        </div>

        <div className="mw-chat-calendar-weekdays">
          {["S", "M", "T", "W", "T", "F", "S"].map((day, index) => (
            <div key={index} className="mw-chat-calendar-weekday">{day}</div>
          ))}
        </div>

        <div className="mw-chat-calendar-days">{renderCalendar()}</div>

        {selectedDate && (
          <div className="mw-chat-calendar-selected">
            Selected: {formatDate(selectedDate)}
          </div>
        )}
      </div>

      {/* Time Slots Section */}
      <div className="mw-chat-timeslots">
        <h3 className="mw-chat-meeting-section-title">Select Time</h3>

        {!selectedDate ? (
          <div className="mw-chat-timeslots-empty">
            <Clock size={24} />
            <p>Please select a date first</p>
          </div>
        ) : slotsLoading ? (
          <div className="mw-chat-timeslots-loading">
            <div className="mw-chat-spinner"></div>
            <p>Loading available slots...</p>
          </div>
        ) : availableSlots.length === 0 ? (
          <div className="mw-chat-timeslots-empty">
            <p>No available slots for this date</p>
            <p className="mw-chat-timeslots-hint">Please try another date</p>
          </div>
        ) : (
          <div className="mw-chat-timeslots-grid">
            {availableSlots.map((slot) => (
              <button
                key={slot.slot_datetime}
                type="button"
                className={`mw-chat-timeslot ${selectedSlot?.slot_datetime === slot.slot_datetime
                  ? "selected"
                  : ""
                  }`}
                onClick={() => setSelectedSlot(slot)}
              >
                {slot.slot_time}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  // Render step 4: Confirmation
  const renderStep4 = () => (
    <div className="mw-chat-meeting-step">
      <div className="mw-chat-meeting-confirm-header">
        <div className="mw-chat-meeting-confirm-icon">
          <CheckCircle size={32} />
        </div>
        <h3>Confirm Your Booking</h3>
        <p>Please review your meeting details</p>
      </div>

      <div className="mw-chat-meeting-confirm-details">
        <div className="mw-chat-meeting-confirm-row">
          <span className="mw-chat-meeting-confirm-label">Meeting Type:</span>
          <span className="mw-chat-meeting-confirm-value">{meetingType}</span>
        </div>

        <div className="mw-chat-meeting-confirm-row">
          <span className="mw-chat-meeting-confirm-label">Topic:</span>
          <span className="mw-chat-meeting-confirm-value">
            {topic === "Other" ? customTopic : topic}
          </span>
        </div>

        <div className="mw-chat-meeting-confirm-row">
          <span className="mw-chat-meeting-confirm-label">Name:</span>
          <span className="mw-chat-meeting-confirm-value">{name}</span>
        </div>

        <div className="mw-chat-meeting-confirm-row">
          <span className="mw-chat-meeting-confirm-label">Contact:</span>
          <span className="mw-chat-meeting-confirm-value">
            {otpMethod === "mobile" ? getFullPhone() : email}
          </span>
        </div>

        {selectedDate && selectedSlot && (
          <>
            <div className="mw-chat-meeting-confirm-row">
              <span className="mw-chat-meeting-confirm-label">Date:</span>
              <span className="mw-chat-meeting-confirm-value">
                {formatDate(selectedDate)}
              </span>
            </div>

            <div className="mw-chat-meeting-confirm-row">
              <span className="mw-chat-meeting-confirm-label">Time:</span>
              <span className="mw-chat-meeting-confirm-value">
                {selectedSlot.slot_time}
              </span>
            </div>
          </>
        )}
      </div>

      <div className="mw-chat-meeting-confirm-actions">
        <button
          type="button"
          className="mw-chat-meeting-btn-secondary"
          onClick={() => {
            setCountdown(5);
            setStep(1);
          }}
          disabled={isSubmitting}
        >
          Back
        </button>

        <button
          type="button"
          className="mw-chat-meeting-btn-primary"
          onClick={bookMeeting}
          disabled={isSubmitting}
        >
          {isSubmitting
            ? "Booking..."
            : `Confirm & Book Meeting (${countdown}s)`}
        </button>
      </div>

      <p className="mw-chat-meeting-disclaimer">
        By confirming, you agree to receive meeting reminders via{" "}
        {otpMethod === "mobile" ? "SMS" : "email"}.
      </p>
    </div>
  );

  // Render success screen
  const renderSuccess = () => (
    <div className="mw-chat-meeting-success">
      <div className="mw-chat-meeting-success-icon">
        <CheckCircle size={48} />
      </div>

      <h2>Meeting Booked Successfully!</h2>

      {bookedMeeting && (
        <div className="mw-chat-meeting-success-details">
          <h3>Meeting Details:</h3>
          <div className="mw-chat-meeting-success-info">
            <p>
              <strong>Date & Time:</strong>{" "}
              {new Date(bookedMeeting.meeting_datetime).toLocaleString()}
            </p>
            <p>
              <strong>Duration:</strong> {bookedMeeting.duration_minutes} minutes
            </p>
            <p>
              <strong>Type:</strong> {bookedMeeting.meeting_type}
            </p>
            {bookedMeeting.meeting_link && (
              <p>
                <strong>Meeting Link:</strong>{" "}
                <a
                  href={bookedMeeting.meeting_link}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Join Meeting
                </a>
              </p>
            )}
          </div>
        </div>
      )}

      <div className="mw-chat-meeting-success-email">
        A confirmation email has been sent to <strong>{email}</strong>
      </div>

      <button
        type="button"
        className="mw-chat-meeting-btn-primary"
        onClick={onClose}
      >
        Close
      </button>
    </div>
  );

  return (
    <div className="mw-chat-sheet-overlay">
      <div className="mw-chat-sheet-container mw-chat-meeting-container">
        <ToastContainer
          position="top-center"
          autoClose={3000}
          hideProgressBar={false}
          newestOnTop
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme="light"
          style={{ marginTop: "80px" }}
        />
        <button onClick={onClose} className="mw-chat-meeting-close-btn">
          <X size={22} />
        </button>

        {/* Hero Section */}
        <div className="mw-chat-meeting-hero">
          <h1>{submitStatus === 'success' ? 'Booking Confirmed!' : 'Book Your Consultation'}</h1>
          <div className="mw-chat-meeting-hero-bar"></div>
        </div>

        {/* Main Content */}
        <div className="mw-chat-meeting-content">
          {submitStatus === "success" ? (
            renderSuccess()
          ) : (
            <>
              {/* Step 1 & 3 Combined */}
              {(step === 1 || step === 3) && (
                <div className="mw-chat-meeting-combined">
                  {/* Calendar & Time Slots */}
                  <div className="mw-chat-meeting-panel">
                    {renderStep3()}
                  </div>
                  {/* Form */}
                  <div className="mw-chat-meeting-panel">
                    {renderStep1()}
                  </div>
                </div>
              )}

              {/* Step 4 - Confirmation */}
              {step === 4 && (
                <div className="mw-chat-meeting-panel">
                  {renderStep4()}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ScheduleMeeting;
